import type { reviewCommentRevisionStatusEnum } from '../../db/schema/enums.js'

/**
 * The current text of a review comment — the one body-resolution rule.
 *
 * `editComment` never rewrites `ReviewComment.body`: an edit inserts a draft
 * revision, and publishing that revision only moves `currentRevisionId`
 * (`review-service.ts`). A comment's current text is therefore the published
 * revision `currentRevisionId` points at, and the comment's own `body` only
 * when no revision has ever been published.
 *
 * A leaf module with only a type import, like `review-comment-visibility.ts`
 * and for the same reason: the rule is read on the server by the client's
 * feedback reader (`dashboard-feedback.ts`) and in the browser by the Review
 * tab (`live-review.ts`), so it must not drag the database client into a
 * browser bundle. The admin card modal (`card-review-query.ts`) applies the
 * same rule in SQL, where the lookup is a join rather than a map.
 */

// Derived here rather than imported: `enums.ts` exports no alias for this
// enum, and a type-only `typeof` keeps the derivation erased at runtime.
type ReviewCommentRevisionStatus =
  (typeof reviewCommentRevisionStatusEnum.enumValues)[number]

// `satisfies` pins the literal to the enum: renaming the revision status is
// then a compile error here, not a resolver that silently never matches. The
// parameter below stays `string` on purpose — callers hold wire rows.
const PUBLISHED = 'published' satisfies ReviewCommentRevisionStatus

/**
 * The whole rule behind one closure: build the published-body lookup from the
 * revisions actually read, then resolve each comment through it.
 *
 * Only a `published` revision may serve as current text — the resolver
 * enforces that itself rather than trusting the caller to have pre-filtered:
 * under two separate reads, a `currentRevisionId` captured before a
 * concurrent publish can point at a revision the revision read already sees
 * as superseded, and resolving it would serve wording an edit has already
 * replaced. Callers may still filter their revision *fetch* to published
 * rows as a size optimization; correctness never depends on it.
 */
export function commentBodyResolver(
  revisions: readonly { id: string; status: string; body: string }[],
): (comment: { body: string; currentRevisionId: string | null }) => string {
  const publishedBodyById = new Map(
    revisions
      .filter((revision) => revision.status === PUBLISHED)
      .map((revision) => [revision.id, revision.body]),
  )
  return (comment) => {
    const published =
      comment.currentRevisionId === null
        ? undefined
        : publishedBodyById.get(comment.currentRevisionId)
    return published ?? comment.body
  }
}
